import { ColumnType, TablePaginationConfig } from 'antd/lib/table';
import { ColumnsType, SorterResult, SortOrder, TableCurrentDataSource } from 'antd/lib/table/interface';
import { isFunction } from 'lodash';
import { useMemo } from 'react';
import { OrderEnum } from 'types/common';

import useReactiveQuery, { IQueryWithSearch } from './useReactiveQuery';

interface IUseTableConfig<DataType, RawData> extends IQueryWithSearch {
  columns?: ColumnsType<DataType>;
  dataMapper?: (raw: RawData) => DataType;
  dataReducer?: (acc: any, raw: RawData) => any;
}

const mapSortOrderToQuery = (sortOrder: SortOrder): OrderEnum =>
  sortOrder === 'ascend' ? OrderEnum.ASC : OrderEnum.DESC;
const mapSortQueryToOrder = (query: OrderEnum): SortOrder => (query === OrderEnum.ASC ? 'ascend' : 'descend');

export default function useReactiveQueryTable<RawData = unknown, DataType = unknown>({
  columns: originalColumns,
  graphQLQuery,
  defaultPage = 1,
  defaultOrder,
  mapQueryToVariables,
  dataMapper,
  dataReducer,
  queryProps,
}: IUseTableConfig<DataType, RawData>) {
  const { refetch, loading, fetchMore, data, meta, query, onChangeQuery } = useReactiveQuery<RawData>({
    graphQLQuery,
    mapQueryToVariables,
    queryProps,
    defaultOrder,
  });

  const dataSource = useMemo<DataType[] | undefined>(() => {
    if (dataMapper) return data?.map(dataMapper);
    if (dataReducer) return data?.reduce(dataReducer, {} as any);
    return (data as unknown) as DataType[] | undefined;
  }, [dataMapper, data]);

  const pagination: TablePaginationConfig = useMemo(
    () => ({
      total: meta?.itemCount || 10,
      pageSize: meta?.take || 10,
      defaultCurrent: defaultPage,
      current: meta?.page,
    }),
    [meta],
  );

  const columns = useMemo<ColumnsType<DataType>>(
    () =>
      originalColumns
        ? originalColumns.map((column: ColumnType<DataType>) => {
            let defaultSortOrder: SortOrder = null;

            const dataIndex = column.dataIndex as string;

            if (query?.order?.[dataIndex]) {
              defaultSortOrder = mapSortQueryToOrder(query.order[dataIndex]);
            }

            return {
              ...column,
              defaultSortOrder,
            };
          })
        : [],
    [query, originalColumns],
  );

  const onChange = (
    _pagination: TablePaginationConfig,
    _filter: any,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
    extra: TableCurrentDataSource<DataType>,
  ) => {
    switch (extra.action) {
      case 'paginate': {
        onChangeQuery({
          page: _pagination.current || 1,
        });
        break;
      }
      case 'sort': {
        const [sort] = [sorter].flat();
        let order = '';
        if (!isFunction(sort?.column?.sorter))
          order = sort?.order ? JSON.stringify({ [`${sort.field || ''}`]: mapSortOrderToQuery(sort.order) }) : '';

        onChangeQuery({
          order,
        });
        break;
      }
      case 'filter': // Later logic
      default:
        break;
    }
  };

  return {
    pagination,
    onChange,
    meta,
    dataSource,
    loading,
    columns,
    refetch,
    fetchMore,
  };
}
